import { useCallback, useMemo } from "react";

const FIELD_IDS = ["departureCity", "destinationResort", "departureDate"];

// FloatingAdvancedSelectFormik/FloatingDestinationCombobox utilisent useClick({ event: "mousedown" })
// de floating-ui. Après une interaction physique, pointerTypeRef bloque le .click() programmatique.
// On dispatch mousedown directement pour bypasser ce mécanisme.
// DateCalendarInput utilise un onClick simple → .click() suffit.
const MOUSEDOWN_FIELD_IDS = new Set(["departureCity", "destinationResort"]);

const getFieldValue = (values, fieldId) =>
	fieldId === "departureDate" ? values.travelDates?.departureDate : values[fieldId];

const dispatchMousedown = element =>
	element.dispatchEvent(
		new MouseEvent("mousedown", { view: window, bubbles: true, cancelable: true, button: 0 })
	);

/**
 * Gère la navigation automatique entre les champs du formulaire de recherche :
 * quand l'utilisateur remplit un champ, on ouvre le prochain champ vide.
 * Si un champ précédent est vide (ex : destination remplie sans ville de départ),
 * on y revient en priorité.
 *
 * @param {object} selectRefs - Map { fieldId → React ref } vers les éléments DOM des champs.
 * @returns {function} handleFieldChange(fieldId, setFieldValue, value, values)
 */
const useSearchFieldNavigation = selectRefs => {
	const fieldOrder = useMemo(
		() => FIELD_IDS.map(id => ({ id, ref: selectRefs[id] })),
		[selectRefs]
	);

	return useCallback(
		(fieldId, setFieldValue, value, values) => {
			setFieldValue(fieldId, value);

			if (!value) {
				return;
			}

			const currentIndex = fieldOrder.findIndex(field => field.id === fieldId);
			if (currentIndex === -1) {
				return;
			}

			const prevEmptyField = fieldOrder
				.slice(0, currentIndex)
				.find(field => !getFieldValue(values, field.id));

			if (prevEmptyField?.ref.current) {
				requestAnimationFrame(() => {
					if (!prevEmptyField.ref.current) {
						return;
					}
					dispatchMousedown(prevEmptyField.ref.current);
				});
				return;
			}

			const nextEmptyField = fieldOrder
				.slice(currentIndex + 1)
				.find(field => !getFieldValue(values, field.id) && field.ref.current);

			if (nextEmptyField) {
				requestAnimationFrame(() => {
					if (!nextEmptyField.ref.current) {
						return;
					}
					if (MOUSEDOWN_FIELD_IDS.has(nextEmptyField.id)) {
						dispatchMousedown(nextEmptyField.ref.current);
					} else {
						nextEmptyField.ref.current.click();
					}
				});
			}
		},
		[fieldOrder]
	);
};

export default useSearchFieldNavigation;
